import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Container from '../components/common/Container';
import {
  HeartIcon,
  ShieldCheckIcon,
  ArrowRightIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';

const GivingBack: React.FC = () => {
  const whyEducation = [
    {
      title: 'The Great Equaliser',
      description: 'Education is the single most powerful tool for breaking the cycle of poverty. A child with access to quality education has the foundation to build any future they choose.',
      stat: '3x',
      statLabel: 'higher lifetime earnings for educated children from disadvantaged backgrounds',
    },
    {
      title: 'Ripple Effects',
      description: 'When you educate a child, you transform a family. Educated children are more likely to ensure their own children receive education, creating generational change.',
      stat: '12%',
      statLabel: 'increase in future family income per year of education',
    },
    {
      title: 'Community Transformation',
      description: 'Educated communities have better health outcomes, lower crime rates, and stronger local economies. Education investment returns dividends across society.',
      stat: '50%',
      statLabel: 'reduction in poverty rates with secondary education completion',
    },
  ];

  const charityPartners = [
    {
      name: 'The Smith Family',
      description: 'Australia\'s leading children\'s education charity, supporting disadvantaged children to succeed at school.',
      focus: 'Educational support programs',
      verified: true,
    },
    {
      name: 'Australian Indigenous Education Foundation',
      description: 'Providing Indigenous students with scholarships and support to access quality education.',
      focus: 'Indigenous education equity',
      verified: true,
    },
    {
      name: 'Country Education Foundation',
      description: 'Helping young Australians from regional, rural and remote areas access education and career pathways.',
      focus: 'Regional education access',
      verified: true,
    },
  ];

  return (
    <>
      {/* Hero Section */}
      <section className="relative pt-20 pb-24 bg-gradient-to-b from-[var(--color-midnight)] to-[var(--color-dark-navy)] overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-20 right-10 w-96 h-96 bg-[var(--color-emerald)] rounded-full filter blur-[150px] opacity-20"></div>
          <div className="absolute bottom-20 left-10 w-96 h-96 bg-[var(--color-electric-blue)] rounded-full filter blur-[150px] opacity-20"></div>
        </div>

        <Container className="relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-4xl mx-auto"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6 }}
              className="inline-flex items-center gap-2 mb-6"
            >
              <div className="glass-card px-4 py-2 rounded-full border border-[var(--color-emerald)]/30 flex items-center gap-2">
                <HeartIcon className="w-5 h-5 text-[var(--color-emerald)]" />
                <span className="text-sm font-semibold text-[var(--color-emerald)]">
                  Our Commitment to NSW
                </span>
              </div>
            </motion.div>

            <h1 className="text-5xl md:text-6xl font-black text-white mb-6">
              One for <span className="text-gradient">One</span>
            </h1>
            <p className="text-2xl text-[var(--color-light-gray)] mb-4">
              For every paid license, we will donate one to a child in need.
            </p>
            <p className="text-lg text-[var(--color-medium-gray)] max-w-3xl mx-auto mb-12">
              Education isn't a privilege—it's a right. Our One for One commitment ensures that every subscription will directly fund learning opportunities for NSW's most disadvantaged students.
            </p>

            {/* Commitment Pledge */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="card-premium p-8 max-w-2xl mx-auto"
              style={{ background: 'rgba(16, 185, 129, 0.05)', border: '1px solid rgba(16, 185, 129, 0.2)' }}
            >
              <div className="flex items-center justify-center gap-3 mb-4">
                <HeartIcon className="w-8 h-8 text-[var(--color-emerald)]" />
                <h3 className="text-2xl font-bold text-white">Our Founding Pledge</h3>
              </div>
              <p className="text-lg text-[var(--color-light-gray)] mb-6">
                From day one of our launch, Lumen8 commits to matching every paid subscription with a free license for a student in need—verified and distributed through established NSW education charities.
              </p>
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <div className="text-3xl font-black text-gradient">1:1</div>
                  <div className="text-xs text-[var(--color-medium-gray)]">Match Ratio</div>
                </div>
                <div>
                  <div className="text-3xl font-black text-gradient">NSW</div>
                  <div className="text-xs text-[var(--color-medium-gray)]">Initial Focus</div>
                </div>
                <div>
                  <div className="text-3xl font-black text-gradient">100%</div>
                  <div className="text-xs text-[var(--color-medium-gray)]">Verified</div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </Container>
      </section>

      {/* Why Education Section */}
      <section className="py-20 bg-[var(--color-dark-navy)]">
        <Container>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-black text-white mb-6">
              Why <span className="text-gradient">Education</span> Matters Most
            </h2>
            <p className="text-xl text-[var(--color-light-gray)] max-w-3xl mx-auto">
              While all charitable causes have value, educational investment creates unique multiplier effects that transform individuals, families, and entire communities for generations.
            </p>
          </motion.div>

          <div className="space-y-8">
            {whyEducation.map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="card-premium p-8 md:p-10 hover-glow"
              >
                <div className="grid md:grid-cols-3 gap-8 items-center">
                  <div className="md:col-span-2">
                    <h3 className="text-2xl font-bold text-white mb-4">{item.title}</h3>
                    <p className="text-lg text-[var(--color-light-gray)]">{item.description}</p>
                  </div>
                  <div className="text-center md:text-right">
                    <div className="text-5xl font-black text-gradient mb-2">{item.stat}</div>
                    <div className="text-sm text-[var(--color-medium-gray)]">{item.statLabel}</div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </Container>
      </section>

      {/* The NSW Context */}
      <section className="py-20 bg-gradient-to-b from-[var(--color-dark-navy)] to-[var(--color-midnight)]">
        <Container>
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl font-black text-white mb-6">
                The Reality in <span className="text-gradient">NSW</span>
              </h2>
              <div className="space-y-6 text-[var(--color-light-gray)]">
                <p className="text-lg">
                  Despite being Australia's most populous state, <strong className="text-white">1 in 6 NSW children live in poverty</strong>. For these children, quality educational resources aren't a given—they're a luxury their families can't afford.
                </p>
                <p className="text-lg">
                  In regional and remote NSW, the gap widens further. Students in rural areas are <strong className="text-white">2.5 times less likely</strong> to complete Year 12 than their Sydney counterparts.
                </p>
                <p className="text-lg">
                  Indigenous students in NSW face even greater challenges, with educational outcomes <strong className="text-white">lagging 2-3 years behind</strong> non-Indigenous students on average.
                </p>
                <p className="text-lg">
                  These aren't just statistics—they're children with potential waiting to be unlocked. Every donated license will represent a real student getting access to adaptive, personalised learning that meets them where they are.
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="card-premium p-8">
                <div className="text-center mb-8">
                  <SparklesIcon className="w-12 h-12 text-[var(--color-electric-blue)] mx-auto mb-4" />
                  <h3 className="text-2xl font-bold text-white">The Lumen8 Difference</h3>
                </div>
                <div className="space-y-4">
                  <div className="flex items-start gap-4 p-4 rounded-lg" style={{ background: 'rgba(16, 185, 129, 0.1)' }}>
                    <div className="w-2 h-2 rounded-full bg-[var(--color-emerald)] mt-2"></div>
                    <div>
                      <div className="font-semibold text-white">Adaptive Learning</div>
                      <div className="text-sm text-[var(--color-light-gray)]">Meets students at their level, regardless of starting point</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-4 p-4 rounded-lg" style={{ background: 'rgba(59, 130, 246, 0.1)' }}>
                    <div className="w-2 h-2 rounded-full bg-[var(--color-electric-blue)] mt-2"></div>
                    <div>
                      <div className="font-semibold text-white">NSW Curriculum Aligned</div>
                      <div className="text-sm text-[var(--color-light-gray)]">Maps directly to NSW syllabus from K-12</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-4 p-4 rounded-lg" style={{ background: 'rgba(139, 92, 246, 0.1)' }}>
                    <div className="w-2 h-2 rounded-full bg-[var(--color-violet)] mt-2"></div>
                    <div>
                      <div className="font-semibold text-white">Works Anywhere</div>
                      <div className="text-sm text-[var(--color-light-gray)]">Low bandwidth mode for regional connectivity</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-4 p-4 rounded-lg" style={{ background: 'rgba(245, 158, 11, 0.1)' }}>
                    <div className="w-2 h-2 rounded-full bg-[var(--color-amber)] mt-2"></div>
                    <div>
                      <div className="font-semibold text-white">Self-Paced</div>
                      <div className="text-sm text-[var(--color-light-gray)]">Perfect for students catching up or working around life circumstances</div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="absolute -top-4 -right-4 w-32 h-32 bg-[var(--color-emerald)] rounded-full filter blur-3xl opacity-20"></div>
            </motion.div>
          </div>
        </Container>
      </section>

      {/* How It Will Work */}
      <section className="py-20 bg-[var(--color-midnight)]">
        <Container>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-black text-white mb-6">
              How <span className="text-gradient">One for One</span> Will Work
            </h2>
            <p className="text-xl text-[var(--color-light-gray)] max-w-3xl mx-auto">
              Complete transparency and independent verification will ensure every donation reaches a child who needs it.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-4 gap-6">
            {[
              { step: '01', title: 'You Subscribe', description: 'Choose any Lumen8 plan for your student', icon: '🎓' },
              { step: '02', title: 'We Match', description: 'One license is automatically allocated to our giving pool', icon: '🤝' },
              { step: '03', title: 'Partners Distribute', description: 'Verified charity partners identify students in need', icon: '🏫' },
              { step: '04', title: 'Children Learn', description: 'Disadvantaged students gain full platform access', icon: '✨' },
            ].map((item, index) => (
              <motion.div
                key={item.step}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="card-premium p-6 text-center hover-glow relative"
              >
                <div className="text-4xl mb-4">{item.icon}</div>
                <div className="text-xs font-bold text-[var(--color-electric-blue)] mb-2">STEP {item.step}</div>
                <h3 className="text-lg font-bold text-white mb-2">{item.title}</h3>
                <p className="text-sm text-[var(--color-medium-gray)]">{item.description}</p>
                {index < 3 && (
                  <div className="hidden md:block absolute top-1/2 -right-3 transform -translate-y-1/2 text-[var(--color-medium-gray)]">
                    →
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </Container>
      </section>

      {/* Charity Partners */}
      <section className="py-20 bg-gradient-to-b from-[var(--color-midnight)] to-[var(--color-dark-navy)]">
        <Container>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-black text-white mb-6">
              Potential <span className="text-gradient">Charity Partners</span>
            </h2>
            <p className="text-xl text-[var(--color-light-gray)] max-w-3xl mx-auto">
              We are in discussions with established, independently audited Australian education charities who have the expertise and infrastructure to reach children in need across NSW.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {charityPartners.map((partner, index) => (
              <motion.div
                key={partner.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="card-premium p-8 hover-glow"
              >
                <div className="flex items-center gap-3 mb-4">
                  <h3 className="text-xl font-bold text-white">{partner.name}</h3>
                  {partner.verified && (
                    <ShieldCheckIcon className="w-5 h-5 text-[var(--color-emerald)]" />
                  )}
                </div>
                <p className="text-[var(--color-light-gray)] mb-4">{partner.description}</p>
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs" style={{ background: 'rgba(16, 185, 129, 0.1)' }}>
                  <span className="text-[var(--color-emerald)]">{partner.focus}</span>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            viewport={{ once: true }}
            className="mt-12 card-premium p-8 text-center"
            style={{ background: 'rgba(16, 185, 129, 0.05)', border: '1px solid rgba(16, 185, 129, 0.2)' }}
          >
            <ShieldCheckIcon className="w-12 h-12 text-[var(--color-emerald)] mx-auto mb-4" />
            <h3 className="text-xl font-bold text-white mb-3">Commitment to Transparency</h3>
            <p className="text-[var(--color-light-gray)] max-w-2xl mx-auto">
              All donations will be independently audited and verified. We will publish quarterly impact reports showing exactly where licenses have been distributed, ensuring complete transparency in our giving program.
            </p>
          </motion.div>
        </Container>
      </section>

      {/* Why Not Other Causes */}
      <section className="py-20 bg-[var(--color-dark-navy)]">
        <Container>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="max-w-4xl mx-auto"
          >
            <div className="card-premium p-10 md:p-12">
              <h2 className="text-3xl md:text-4xl font-black text-white mb-8 text-center">
                A Note on <span className="text-gradient">Charitable Giving</span>
              </h2>

              <div className="prose prose-lg prose-invert max-w-none space-y-6 text-[var(--color-light-gray)]">
                <p>
                  We're often asked why we focus exclusively on education rather than other worthy causes. The answer lies in the unique properties of educational investment.
                </p>

                <p>
                  All charitable causes deserve support. Animal welfare, environmental conservation, medical research—these are all important. But education is fundamentally different because of its <strong className="text-white">compounding nature</strong>.
                </p>

                <p>
                  When you feed a hungry child, you solve today's hunger. When you <strong className="text-white">educate</strong> a hungry child, you give them the tools to ensure they and their future children never go hungry again. Education is the intervention that enables all other interventions.
                </p>

                <p>
                  An educated population makes better health decisions, earns more (generating tax revenue for public services), is more civically engaged, and contributes more to every aspect of society. The multiplier effect of educational investment has been documented at <strong className="text-white">7-10x returns</strong> in long-term economic studies.
                </p>

                <p>
                  For those with significant resources to allocate to charitable causes, we'd encourage consideration of how education funding creates cascading benefits that ultimately support <em>all</em> causes—including animal welfare, as educated societies invest more in ethical treatment of animals and environmental stewardship.
                </p>

                <div className="mt-8 p-6 rounded-lg" style={{ background: 'rgba(59, 130, 246, 0.1)', border: '1px solid rgba(59, 130, 246, 0.2)' }}>
                  <p className="text-white font-semibold mb-2 text-center">
                    "The best investment we can make in any society is in the education of its children."
                  </p>
                  <p className="text-sm text-[var(--color-medium-gray)] text-center">
                    — Economic consensus across OECD nations
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </Container>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-b from-[var(--color-dark-navy)] to-[var(--color-midnight)]">
        <Container>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center max-w-3xl mx-auto"
          >
            <HeartIcon className="w-16 h-16 text-[var(--color-emerald)] mx-auto mb-6" />
            <h2 className="text-4xl md:text-5xl font-black text-white mb-6">
              Be Part of <span className="text-gradient">Something Bigger</span>
            </h2>
            <p className="text-xl text-[var(--color-light-gray)] mb-8">
              When Lumen8 launches, every subscription will do double duty—advancing your student's education while giving another child the same opportunity. Together, we'll build a more educated, equitable NSW.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="btn-premium text-lg px-8 py-4 inline-flex items-center justify-center gap-2"
              >
                Get Notified at Launch
                <ArrowRightIcon className="w-5 h-5" />
              </Link>
              <Link
                to="/about"
                className="btn-secondary text-lg px-8 py-4 inline-flex items-center justify-center gap-2"
              >
                Learn More About Us
              </Link>
            </div>
          </motion.div>
        </Container>
      </section>
    </>
  );
};

export default GivingBack;
